/**
 * AI Score Badge Component
 * Displays AI lead score with color coding and optional factor breakdown
 */

import React from 'react';
import { Box, Tooltip, Typography, Paper, LinearProgress, Stack } from '@mui/material';
import { TrendingUp, TrendingDown } from '@mui/icons-material';

interface ScoringFactors {
  engagement?: number;
  budget?: number;
  authority?: number;
  need?: number;
  timing?: number;
}

interface AIAnalysis {
  summary?: string;
  confidence?: number;
  reasoning?: string;
  strengths?: string[];
  weaknesses?: string[];
}

interface AIScoreBadgeProps {
  score: number;
  factors?: ScoringFactors;
  analysis?: AIAnalysis;
  suggestedActions?: string[];
  conversionProbability?: number;
  estimatedDaysToClose?: number;
  variant?: 'compact' | 'detailed' | 'full';
  size?: 'small' | 'medium' | 'large';
  showTrend?: boolean;
  previousScore?: number;
}

const AIScoreBadge: React.FC<AIScoreBadgeProps> = ({
  score,
  factors,
  analysis,
  suggestedActions,
  conversionProbability,
  estimatedDaysToClose,
  variant = 'compact',
  size = 'medium',
  showTrend = false,
  previousScore
}) => {
  // Determine color based on score
  const getScoreColor = (score: number) => {
    if (score >= 80) return { bg: '#52c41a', text: '#fff' }; // Green
    if (score >= 60) return { bg: '#faad14', text: '#fff' }; // Yellow/Orange
    return { bg: '#ff4d4f', text: '#fff' }; // Red
  };

  // Determine size dimensions
  const getSizeDimensions = () => {
    switch (size) {
      case 'small':
        return { width: 40, height: 40, fontSize: '14px', fontWeight: 600 };
      case 'large':
        return { width: 80, height: 80, fontSize: '24px', fontWeight: 700 };
      default: // medium
        return { width: 60, height: 60, fontSize: '18px', fontWeight: 700 };
    }
  };

  const colors = getScoreColor(score);
  const dimensions = getSizeDimensions();

  // Calculate trend
  const trend = previousScore !== undefined ? score - previousScore : null;
  const trendIcon = trend !== null && trend > 0 ? <TrendingUp sx={{ fontSize: 14 }} /> :
                    trend !== null && trend < 0 ? <TrendingDown sx={{ fontSize: 14 }} /> : null;

  // Tooltip content with scoring factors
  const tooltipContent = factors ? (
    <Box sx={{ p: 1 }}>
      <Typography variant="body2" fontWeight="bold" mb={1}>
        Scoring Factors
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {factors.engagement !== undefined && (
          <Typography variant="caption">
            Engagement: {factors.engagement}%
          </Typography>
        )}
        {factors.budget !== undefined && (
          <Typography variant="caption">
            Budget: {factors.budget}%
          </Typography>
        )}
        {factors.authority !== undefined && (
          <Typography variant="caption">
            Authority: {factors.authority}%
          </Typography>
        )}
        {factors.need !== undefined && (
          <Typography variant="caption">
            Need: {factors.need}%
          </Typography>
        )}
        {factors.timing !== undefined && (
          <Typography variant="caption">
            Timing: {factors.timing}%
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ mt: 1, display: 'block', color: '#aaa' }}>
        Total Score: {score}/100
      </Typography>
    </Box>
  ) : (
    <Typography variant="body2">AI Score: {score}/100</Typography>
  );

  const badgeElement = (
    <Box
      sx={{
        width: dimensions.width,
        height: dimensions.height,
        borderRadius: '50%',
        backgroundColor: colors.bg,
        color: colors.text,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: dimensions.fontSize,
        fontWeight: dimensions.fontWeight,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        position: 'relative',
        cursor: factors ? 'pointer' : 'default',
      }}
    >
      {score}
      {showTrend && trend !== null && (
        <Box
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            backgroundColor: trend > 0 ? '#52c41a' : '#ff4d4f',
            borderRadius: '50%',
            width: 20,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
          }}
        >
          {trendIcon}
        </Box>
      )}
    </Box>
  );

  // Factor config with max values and colors
  const factorConfig = {
    engagement: { label: 'Engagement 互動', max: 30, color: '#1976d2' },
    budget: { label: 'Budget 預算', max: 20, color: '#2e7d32' },
    authority: { label: 'Authority 決策權', max: 30, color: '#ed6c02' },
    need: { label: 'Need 需求', max: 10, color: '#9c27b0' },
    timing: { label: 'Timing 時機', max: 10, color: '#0288d1' },
  };

  // Detailed variant with factors breakdown (progress bars)
  if (variant === 'detailed' && factors) {
    return (
      <Paper
        elevation={2}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {badgeElement}
        <Box sx={{ width: '100%' }}>
          <Typography variant="body2" fontWeight="bold" mb={1} textAlign="center">
            Scoring Factors 評分因素
          </Typography>
          <Stack spacing={1.5}>
            {Object.entries(factors).map(([key, value]) => {
              if (value === undefined) return null;
              const config = factorConfig[key as keyof typeof factorConfig];
              if (!config) return null;
              const percentage = (value / config.max) * 100;
              return (
                <Box key={key}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {config.label}
                    </Typography>
                    <Typography variant="caption" fontWeight="bold">
                      {value}/{config.max}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(percentage, 100)}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: config.color,
                        borderRadius: 3,
                      },
                    }}
                  />
                </Box>
              );
            })}
          </Stack>
        </Box>
      </Paper>
    );
  }

  // Full variant with all AI insights
  if (variant === 'full') {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Stack spacing={2}>
          {/* Score Badge and Summary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {badgeElement}
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                AI Score 評分: {score}/100
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {score >= 80 ? 'High potential 高潛力' : score >= 60 ? 'Medium potential 中等潛力' : 'Low potential 低潛力'}
              </Typography>
              {conversionProbability !== undefined && (
                <Typography variant="caption" color="primary">
                  Conversion probability 轉換機率: {conversionProbability}%
                </Typography>
              )}
            </Box>
          </Box>

          {/* Scoring Factors */}
          {factors && (
            <Box>
              <Typography variant="body2" fontWeight="bold" mb={1}>
                Scoring Factors 評分因素
              </Typography>
              <Stack spacing={1}>
                {Object.entries(factors).map(([key, value]) => {
                  if (value === undefined) return null;
                  const config = factorConfig[key as keyof typeof factorConfig];
                  if (!config) return null;
                  const percentage = (value / config.max) * 100;
                  return (
                    <Box key={key}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          {config.label}
                        </Typography>
                        <Typography variant="caption" fontWeight="bold">
                          {value}/{config.max}
                        </Typography>
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(percentage, 100)}
                        sx={{
                          height: 6,
                          borderRadius: 3,
                          backgroundColor: 'grey.200',
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: config.color,
                            borderRadius: 3,
                          },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}

          {/* AI Analysis */}
          {analysis?.reasoning && (
            <Box>
              <Typography variant="body2" fontWeight="bold" mb={0.5}>
                AI Analysis AI分析
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {analysis.reasoning}
              </Typography>
            </Box>
          )}

          {/* Suggested Actions */}
          {suggestedActions && suggestedActions.length > 0 && (
            <Box>
              <Typography variant="body2" fontWeight="bold" mb={0.5}>
                Suggested Actions 建議行動
              </Typography>
              <Stack spacing={0.5}>
                {suggestedActions.slice(0, 3).map((action, index) => (
                  <Typography key={index} variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                    • {action}
                  </Typography>
                ))}
              </Stack>
            </Box>
          )}

          {/* Estimated Days to Close */}
          {estimatedDaysToClose !== undefined && (
            <Typography variant="caption" color="text.secondary">
              Estimated time to close 預計成交時間: ~{estimatedDaysToClose} days 天
            </Typography>
          )}
        </Stack>
      </Paper>
    );
  }

  // Compact variant with tooltip
  return factors ? (
    <Tooltip title={tooltipContent} arrow placement="top">
      {badgeElement}
    </Tooltip>
  ) : (
    badgeElement
  );
};

export default AIScoreBadge;
